// Where an investigation landed on the far side (#818): its tickets and its Slack posts,
// linked out in a new tab, the same way on every surface that shows an investigation —
// the run page, the investigations list and an approval card.

import type { RunTicket, SlackPost } from "../api/runRecord";
import { slackPermalink } from "./connectionHealth";

const QUEUE_NAMES: Record<string, string> = { jira: "Jira" };

/** The queue a ticket is in, in words. */
export function queueName(kind: string): string {
  return QUEUE_NAMES[kind] ?? kind;
}

/** One ticket's key, linking to it when the queue gave a link, and never inventing one. */
export function TicketLink({ ticket }: { ticket: RunTicket }) {
  if (!ticket.url) return <code>{ticket.key}</code>;
  return (
    <a
      href={ticket.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${queueName(ticket.kind)} ${ticket.key}, opens in a new tab`}
    >
      {ticket.key}
    </a>
  );
}

/** A run's tickets, comma separated. Nothing at all when it has none. */
export function TicketLinks({ tickets }: { tickets: readonly RunTicket[] }) {
  if (tickets.length === 0) return null;
  return (
    <>
      {tickets.map((ticket, index) => (
        <span key={`${ticket.kind}:${ticket.key}`}>
          {index > 0 ? ", " : null}
          <TicketLink ticket={ticket} />
        </span>
      ))}
    </>
  );
}

function sentence(words: string): string {
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** One Slack message, named by the event it carried, linking to it in Slack. */
export function SlackPostLink({ post }: { post: SlackPost }) {
  const what = sentence(post.event ?? "message");
  return (
    <a
      href={slackPermalink(post.channel, post.ts)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Slack message: ${what}, opens in a new tab`}
    >
      {what}
    </a>
  );
}
